import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetClientMarketQueryResponse, GetClientMarketPathParams, GetClientMarket404 } from "../types/GetClientMarket";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetClientMarketClient = typeof client<GetClientMarketQueryResponse, GetClientMarket404, never>;
type GetClientMarket = {
    data: GetClientMarketQueryResponse;
    error: GetClientMarket404;
    request: never;
    pathParams: GetClientMarketPathParams;
    queryParams: never;
    headerParams: never;
    response: GetClientMarketQueryResponse;
    client: {
        parameters: Partial<Parameters<GetClientMarketClient>[0]>;
        return: Awaited<ReturnType<GetClientMarketClient>>;
    };
};
export const getClientMarketQueryKey = (clientCode: GetClientMarketPathParams["clientCode"], marketCode: GetClientMarketPathParams["marketCode"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode", params: { clientCode: clientCode, marketCode: marketCode } }] as const;
export type GetClientMarketQueryKey = ReturnType<typeof getClientMarketQueryKey>;
export function getClientMarketQueryOptions(clientCode: GetClientMarketPathParams["clientCode"], marketCode: GetClientMarketPathParams["marketCode"], options: GetClientMarket["client"]["parameters"] = {}) {
    const queryKey = getClientMarketQueryKey(clientCode, marketCode);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetClientMarket["data"], GetClientMarket["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single market by code for a given client
 * @summary Get
 * @link /api/clients/:clientCode/markets/:marketCode
 */
export function useGetClientMarket<TData = GetClientMarket["response"], TQueryData = GetClientMarket["response"], TQueryKey extends QueryKey = GetClientMarketQueryKey>(clientCode: GetClientMarketPathParams["clientCode"], marketCode: GetClientMarketPathParams["marketCode"], options: {
    query?: Partial<QueryObserverOptions<GetClientMarket["response"], GetClientMarket["error"], TData, TQueryData, TQueryKey>>;
    client?: GetClientMarket["client"]["parameters"];
} = {}): UseQueryResult<TData, GetClientMarket["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getClientMarketQueryKey(clientCode, marketCode);
    const query = useQuery({
        ...getClientMarketQueryOptions(clientCode, marketCode, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetClientMarket["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getClientMarketSuspenseQueryKey = (clientCode: GetClientMarketPathParams["clientCode"], marketCode: GetClientMarketPathParams["marketCode"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode", params: { clientCode: clientCode, marketCode: marketCode } }] as const;
export type GetClientMarketSuspenseQueryKey = ReturnType<typeof getClientMarketSuspenseQueryKey>;
export function getClientMarketSuspenseQueryOptions(clientCode: GetClientMarketPathParams["clientCode"], marketCode: GetClientMarketPathParams["marketCode"], options: GetClientMarket["client"]["parameters"] = {}) {
    const queryKey = getClientMarketSuspenseQueryKey(clientCode, marketCode);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetClientMarket["data"], GetClientMarket["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets a single market by code for a given client
 * @summary Get
 * @link /api/clients/:clientCode/markets/:marketCode
 */
export function useGetClientMarketSuspense<TData = GetClientMarket["response"], TQueryKey extends QueryKey = GetClientMarketSuspenseQueryKey>(clientCode: GetClientMarketPathParams["clientCode"], marketCode: GetClientMarketPathParams["marketCode"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetClientMarket["response"], GetClientMarket["error"], TData, TQueryKey>>;
    client?: GetClientMarket["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetClientMarket["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getClientMarketSuspenseQueryKey(clientCode, marketCode);
    const query = useSuspenseQuery({
        ...getClientMarketSuspenseQueryOptions(clientCode, marketCode, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetClientMarket["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}